import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { LiveQuizResultsDTO, StudentQuizResult, getLiveQuizResults } from '../../services/tutorSessionService';

interface Props {
  courseId: number;
  quizSessionId: number;
  isActive: boolean;
  questionCount: number;
}

export default function LiveQuizDispatch({ courseId, quizSessionId, isActive, questionCount }: Props) {
  const [results, setResults] = useState<LiveQuizResultsDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function fetchResults() {
    try {
      const data = await getLiveQuizResults(courseId, quizSessionId);
      setResults(data);
      setError(null);
    } catch {
      setError('Failed to load results.');
    }
  }

  useEffect(() => {
    fetchResults();
    if (isActive) {
      intervalRef.current = setInterval(fetchResults, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [quizSessionId, isActive]);

  const submitted = results?.studentResults ?? [];

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Live Results — {submitted.length} submitted
        {isActive && (
          <Chip label="LIVE" color="error" size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
        )}
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

      {submitted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Waiting for submissions…
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell align="center">Submitted</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {submitted.map((r: StudentQuizResult) => (
              <TableRow key={r.studentId}>
                <TableCell>{r.studentName}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    {r.score === questionCount ? (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    ) : (
                      <CancelOutlinedIcon color="error" fontSize="small" />
                    )}
                    <Typography variant="body2">{r.score}/{questionCount}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" color="text.secondary">
                    {new Date(r.submittedAt).toLocaleTimeString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
